import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DataTable } from '../components/ui/data-table';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  Play,
  Calendar,
  ChevronRight,
  RefreshCw,
  Wrench,
  Ticket,
  Package,
  Timer,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';

const StatusPage = () => {
  const navigate = useNavigate();
  const { selectedPlant, selectedLocation } = useData();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('delayed');
  
  // Data states
  const [delayedItems, setDelayedItems] = useState({ tickets: [], material_requests: [], revisions: [] });
  const [inProgressItems, setInProgressItems] = useState([]);
  const [todayItems, setTodayItems] = useState([]);
  const [upcomingItems, setUpcomingItems] = useState({ preventive: [], corrective: [], predictive: [] });

  useEffect(() => {
    fetchAllData();
  }, [selectedPlant, selectedLocation]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (selectedLocation && selectedLocation !== 'all') params.location_id = selectedLocation;

      const [delayedRes, inProgressRes, todayRes, upcomingRes] = await Promise.all([
        api.get('/status/delayed', { params }),
        api.get('/status/in-progress', { params }),
        api.get('/status/today', { params }),
        api.get('/status/upcoming', { params })
      ]);

      setDelayedItems(delayedRes.data);
      setInProgressItems(inProgressRes.data);
      setTodayItems(todayRes.data);
      setUpcomingItems(upcomingRes.data);
    } catch (error) {
      console.error('Error fetching status data:', error);
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      planned: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-slate-100 text-slate-700',
      rejected: 'bg-red-100 text-red-700',
      waiting_parts: 'bg-orange-100 text-orange-700'
    };
    const labels = {
      pending: 'In asteptare',
      accepted: 'Acceptat',
      in_progress: 'In lucru',
      planned: 'Planificat',
      completed: 'Finalizat',
      cancelled: 'Anulat',
      rejected: 'Respins',
      waiting_parts: 'Asteapta piese'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    const labels = {
      critical: 'Critic',
      high: 'Ridicat',
      medium: 'Mediu',
      low: 'Scazut'
    };
    return <Badge className={`${styles[priority] || 'bg-slate-100'} border`}>{labels[priority] || priority}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHoursLate = (dateStr) => {
    if (!dateStr) return 0;
    const diff = Date.now() - new Date(dateStr).getTime();
    return Math.floor(diff / (1000 * 60 * 60));
  };

  // Summary cards data
  const summaryData = {
    delayed: {
      tickets: delayedItems.tickets?.length || 0,
      requests: delayedItems.material_requests?.length || 0,
      revisions: delayedItems.revisions?.length || 0
    },
    inProgress: inProgressItems.length,
    today: todayItems.length,
    upcoming: (upcomingItems.preventive?.length || 0) + 
              (upcomingItems.corrective?.length || 0) + 
              (upcomingItems.predictive?.length || 0)
  };

  // Table columns for delayed tickets
  const delayedTicketColumns = [
    {
      key: 'created_at',
      label: 'Data',
      sortable: true,
      render: (row) => row ? (
        <div>
          <div className="font-medium">{formatDate(row.created_at)}</div>
          <div className="text-xs text-red-600">{getHoursLate(row.created_at)}h intarziere</div>
        </div>
      ) : '-'
    },
    { key: 'subject', label: 'Subiect', sortable: true },
    { key: 'requester_name', label: 'Solicitant', sortable: true },
    { key: 'equipment_name', label: 'Echipament', sortable: true },
    {
      key: 'priority',
      label: 'Prioritate',
      sortable: true,
      render: (row) => row ? getPriorityBadge(row.priority) : '-'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => row ? getStatusBadge(row.status) : '-'
    }
  ];

  // Table columns for material requests
  const materialRequestColumns = [
    {
      key: 'created_at',
      label: 'Data',
      sortable: true,
      render: (row) => row ? (
        <div>
          <div className="font-medium">{formatDate(row.created_at)}</div>
          <div className="text-xs text-red-600">{getHoursLate(row.created_at)}h intarziere</div>
        </div>
      ) : '-'
    },
    { key: 'requester_name', label: 'Solicitant', sortable: true },
    {
      key: 'importance',
      label: 'Importanta',
      sortable: true,
      render: (row) => row ? getPriorityBadge(row.importance || 'medium') : '-'
    },
    { key: 'items_count', label: 'Nr. articole', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => row ? getStatusBadge(row.status) : '-'
    }
  ];

  // Table columns for in-progress revisions
  const inProgressColumns = [
    {
      key: 'started_at',
      label: 'Inceput',
      sortable: true,
      render: (row) => row ? formatDate(row.started_at || row.scheduled_date) : '-'
    },
    { key: 'title', label: 'Nume revizie', sortable: true },
    { key: 'equipment_name', label: 'Echipament', sortable: true },
    {
      key: 'revision_type',
      label: 'Tip',
      sortable: true,
      render: (row) => {
        if (!row) return '-';
        const types = { preventive: 'Preventiva', corrective: 'Corectiva', predictive: 'Predictiva' };
        return types[row.revision_type] || row.revision_type;
      }
    },
    { key: 'assigned_to_name', label: 'Responsabil', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => row ? getStatusBadge(row.status) : '-'
    }
  ];

  // Table columns for today's schedule
  const todayColumns = [
    {
      key: 'scheduled_date',
      label: 'Ora planificata',
      sortable: true,
      render: (row) => row?.scheduled_date ? new Date(row.scheduled_date).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '-'
    },
    { key: 'title', label: 'Nume', sortable: true },
    { key: 'equipment_name', label: 'Echipament', sortable: true },
    {
      key: 'revision_type',
      label: 'Tip',
      sortable: true,
      render: (row) => {
        if (!row) return '-';
        const types = { preventive: 'Preventiva', corrective: 'Corectiva', predictive: 'Predictiva' };
        return types[row.revision_type] || row.revision_type;
      }
    },
    { key: 'assigned_to_name', label: 'Responsabil', sortable: true },
    {
      key: 'estimated_hours',
      label: 'Durata est.',
      sortable: true,
      render: (row) => row?.estimated_hours ? `${row.estimated_hours}h` : '-'
    }
  ];

  // Table columns for upcoming revisions
  const upcomingColumns = [
    {
      key: 'scheduled_date',
      label: 'Data planificata',
      sortable: true,
      render: (row) => row ? formatDate(row.scheduled_date) : '-'
    },
    { key: 'title', label: 'Nume revizie', sortable: true },
    { key: 'equipment_name', label: 'Echipament', sortable: true },
    { key: 'assigned_to_name', label: 'Responsabil', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => row ? getStatusBadge(row.status) : '-'
    }
  ];

  const handleRowClick = (item, type) => {
    if (type === 'ticket') {
      navigate(`/tickets?id=${item.id}`);
    } else if (type === 'material_request') {
      navigate(`/material-requests?id=${item.id}`);
    } else {
      navigate(`/revisions/${item.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="status-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Status Operational</h1>
          <p className="text-slate-600 mt-1">Monitorizare situatii intarziate, in lucru si planificate</p>
        </div>
        <Button onClick={fetchAllData} variant="outline" className="h-11" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizeaza
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`card-industrial cursor-pointer transition-all ${activeTab === 'delayed' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => setActiveTab('delayed')}
              data-testid="delayed-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {summaryData.delayed.tickets + summaryData.delayed.requests + summaryData.delayed.revisions}
                </p>
                <p className="text-sm text-slate-600">Intarziate</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs text-slate-500">
              <div className="text-center">
                <span className="block font-semibold text-slate-700">{summaryData.delayed.tickets}</span>
                Tichete
              </div>
              <div className="text-center">
                <span className="block font-semibold text-slate-700">{summaryData.delayed.requests}</span>
                Cereri
              </div>
              <div className="text-center">
                <span className="block font-semibold text-slate-700">{summaryData.delayed.revisions}</span>
                Revizii
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-industrial cursor-pointer transition-all ${activeTab === 'in-progress' ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setActiveTab('in-progress')}
              data-testid="in-progress-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <Play className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{summaryData.inProgress}</p>
                <p className="text-sm text-slate-600">In lucru</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-industrial cursor-pointer transition-all ${activeTab === 'today' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setActiveTab('today')}
              data-testid="today-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{summaryData.today}</p>
                <p className="text-sm text-slate-600">Ordinea de zi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-industrial cursor-pointer transition-all ${activeTab === 'upcoming' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setActiveTab('upcoming')}
              data-testid="upcoming-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{summaryData.upcoming}</p>
                <p className="text-sm text-slate-600">Urmatoarele</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 h-12">
          <TabsTrigger value="delayed" className="h-10 text-xs md:text-sm" data-testid="tab-delayed">
            <AlertTriangle className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Situatii</span> Intarziate
          </TabsTrigger>
          <TabsTrigger value="in-progress" className="h-10 text-xs md:text-sm" data-testid="tab-in-progress">
            <Play className="w-4 h-4 mr-1 md:mr-2" />
            In Lucru
          </TabsTrigger>
          <TabsTrigger value="today" className="h-10 text-xs md:text-sm" data-testid="tab-today">
            <Clock className="w-4 h-4 mr-1 md:mr-2" />
            Ordinea de Zi
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="h-10 text-xs md:text-sm" data-testid="tab-upcoming">
            <Calendar className="w-4 h-4 mr-1 md:mr-2" />
            Urmatoarele
          </TabsTrigger>
        </TabsList>

        {/* Delayed Items Tab */}
        <TabsContent value="delayed" className="mt-4 space-y-6">
          {/* Delayed Tickets */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-red-600" />
                <CardTitle className="text-lg">Tichete Intarziate ({delayedItems.tickets?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {delayedItems.tickets?.length > 0 ? (
                <DataTable
                  data={delayedItems.tickets}
                  columns={delayedTicketColumns}
                  onRowClick={(row) => handleRowClick(row, 'ticket')}
                  rowClassName="cursor-pointer hover:bg-red-50"
                />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>Nu exista tichete intarziate</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delayed Material Requests */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-red-600" />
                <CardTitle className="text-lg">Cereri Materiale Intarziate ({delayedItems.material_requests?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {delayedItems.material_requests?.length > 0 ? (
                <DataTable
                  data={delayedItems.material_requests}
                  columns={materialRequestColumns}
                  onRowClick={(row) => handleRowClick(row, 'material_request')}
                  rowClassName="cursor-pointer hover:bg-red-50"
                />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>Nu exista cereri materiale intarziate</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delayed Revisions */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-red-600" />
                <CardTitle className="text-lg">Revizii Intarziate ({delayedItems.revisions?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {delayedItems.revisions?.length > 0 ? (
                <DataTable
                  data={delayedItems.revisions}
                  columns={inProgressColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-red-50"
                />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>Nu exista revizii intarziate</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* In Progress Tab */}
        <TabsContent value="in-progress" className="mt-4">
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Play className="w-5 h-5 text-purple-600" />
                <CardTitle className="text-lg">Activitati in Lucru ({inProgressItems.length})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {inProgressItems.length > 0 ? (
                <DataTable
                  data={inProgressItems}
                  columns={inProgressColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-purple-50"
                />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Timer className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nu exista activitati in lucru</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Today's Schedule Tab */}
        <TabsContent value="today" className="mt-4">
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">
                  Ordinea de Zi - {new Date().toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {todayItems.length > 0 ? (
                <DataTable
                  data={todayItems}
                  columns={todayColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-blue-50"
                />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nu exista activitati planificate pentru astazi</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upcoming Tab */}
        <TabsContent value="upcoming" className="mt-4 space-y-6">
          {/* Preventive */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-600" />
                <CardTitle className="text-lg">Mentenanta Preventiva ({upcomingItems.preventive?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingItems.preventive?.length > 0 ? (
                <DataTable
                  data={upcomingItems.preventive}
                  columns={upcomingColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-green-50"
                />
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p>Nu exista revizii preventive programate</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Corrective */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <CardTitle className="text-lg">Mentenanta Corectiva ({upcomingItems.corrective?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingItems.corrective?.length > 0 ? (
                <DataTable
                  data={upcomingItems.corrective}
                  columns={upcomingColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-orange-50"
                />
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p>Nu exista revizii corective programate</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Predictive */}
          <Card className="card-industrial">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Timer className="w-5 h-5 text-cyan-600" />
                <CardTitle className="text-lg">Mentenanta Predictiva ({upcomingItems.predictive?.length || 0})</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingItems.predictive?.length > 0 ? (
                <DataTable
                  data={upcomingItems.predictive}
                  columns={upcomingColumns}
                  onRowClick={(row) => handleRowClick(row, 'revision')}
                  rowClassName="cursor-pointer hover:bg-cyan-50"
                />
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p>Nu exista revizii predictive programate</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StatusPage;
